/**
 * MapContextMenu — Right-click / long-press context menu for Leaflet map markers.
 *
 * Custom implementation (not Radix-based) because:
 *   - Leaflet markers render outside React's DOM tree
 *   - Position-based at cursor coordinates {x, y}, not anchor-based
 *   - Long-press detection happens in MapViewLeaflet via Leaflet events
 *
 * For DOM-element right-click menus, use <ContextMenu> from
 * components/overlay/ContextMenu.jsx (Radix-based).
 *
 * Two modes:
 *   - 'field' — opened on a field marker (10 actions)
 *   - 'empty' — opened on empty map area (7 actions)
 *
 * Z-index: --aif-z-contextual (1600) — sits above all page-level overlays.
 *
 * Accessibility:
 *   - role="menu" on container, aria-label provided
 *   - role="menuitem" on each button, role="separator" on dividers
 *   - Arrow Up/Down navigates with roving tabIndex
 *   - Enter activates focused item
 *   - Escape closes
 *   - Auto-focuses first item on open
 *   - Click outside closes
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.7
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function useMapMenuItems() {
  const { t } = useTranslation();
  const FIELD_ITEMS = [
    { id: 'task',         icon: '\uD83D\uDCCB', label: t('mapMenu.createTask') },
    { id: 'activity',     icon: '\uD83D\uDE9C', label: t('mapMenu.documentActivity') },
    { id: 'photo',        icon: '\uD83D\uDCF8', label: t('mapMenu.uploadPhoto') },
    { id: 'share_worker', icon: '\uD83D\uDC64', label: t('mapMenu.shareWorker') },
    { id: 'share_ext',    icon: '\uD83D\uDD17', label: t('mapMenu.shareExternal'), muted: true },
    { id: 'divider' },
    { id: 'edit_field',   icon: '\u270F\uFE0F', label: t('mapMenu.editField') },
    { id: 'delete_field', icon: '\uD83D\uDDD1\uFE0F', label: t('mapMenu.deleteField'), danger: true },
    { id: 'divider2' },
    { id: 'detail',       icon: '\u2139\uFE0F', label: t('mapMenu.fieldDetails') },
  ];

  const EMPTY_ITEMS = [
    { id: 'draw_field',    icon: '\uD83D\uDD8A\uFE0F', label: t('mapMenu.drawField') },
    { id: 'add_poi',       icon: '\uD83D\uDCCD', label: t('mapMenu.addPoi') },
    { id: 'add_delivery',  icon: '\uD83D\uDE9A', label: t('mapMenu.addDelivery') },
    { id: 'photo',         icon: '\uD83D\uDCF8', label: t('mapMenu.uploadPhoto'), muted: true },
    { id: 'divider' },
    { id: 'import_fields', icon: '\uD83D\uDCC2', label: t('mapMenu.importFields') },
    { id: 'manage_fields', icon: '\uD83D\uDCCB', label: t('mapMenu.manageFields') },
  ];

  return { FIELD_ITEMS, EMPTY_ITEMS };
}

export default function MapContextMenu({
  mode = 'field',
  field,
  x,
  y,
  onClose,
  onCreateTask,
  onCreateActivity,
  onUploadPhoto,
  onShareWorker,
  onShareExternal,
  onOpenDetail,
  onDrawField,
  onAddPoi,
  onEditField,
  onDeleteField,
  onManageFields,
  onImportFields,
}) {
  const menuRef = useRef(null);

  // Auto-focus first menuitem on mount
  useEffect(() => {
    if (menuRef.current) {
      const first = menuRef.current.querySelector('[role="menuitem"]');
      if (first) first.focus();
    }
  }, []);

  // Escape + click outside
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('pointerdown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('pointerdown', handleClick);
    };
  }, [onClose]);

  // Arrow key navigation (roving focus)
  const handleKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const menuItems = Array.from(
      menuRef.current?.querySelectorAll('[role="menuitem"]') || []
    );
    if (!menuItems.length) return;
    const current = menuItems.indexOf(document.activeElement);
    let next;
    if (e.key === 'ArrowDown') {
      next = current < menuItems.length - 1 ? current + 1 : 0;
    } else {
      next = current > 0 ? current - 1 : menuItems.length - 1;
    }
    menuItems[next].focus();
  };

  const handleItem = (id) => {
    onClose();
    if (mode === 'empty') {
      switch (id) {
        case 'draw_field':    onDrawField?.(); break;
        case 'add_poi':       onAddPoi?.(); break;
        case 'add_delivery':  onAddPoi?.('delivery'); break;
        case 'photo':         onUploadPhoto?.(); break;
        case 'import_fields': onImportFields?.(); break;
        case 'manage_fields': onManageFields?.(); break;
      }
      return;
    }
    // mode === 'field'
    switch (id) {
      case 'task':          onCreateTask?.(field.id); break;
      case 'activity':      onCreateActivity?.(field.id); break;
      case 'photo':         onUploadPhoto?.(field.id); break;
      case 'share_worker':  onShareWorker?.(field.id); break;
      case 'share_ext':     onShareExternal?.(field.id); break;
      case 'detail':        onOpenDetail?.(field.id); break;
      case 'edit_field':    onEditField?.(field); break;
      case 'delete_field':  onDeleteField?.(field); break;
    }
  };

  const { t } = useTranslation();
  const { FIELD_ITEMS, EMPTY_ITEMS } = useMapMenuItems();
  const safeX = Math.min(x, window.innerWidth - 230);
  const safeY = Math.min(y, window.innerHeight - 340);
  const items = mode === 'empty' ? EMPTY_ITEMS : FIELD_ITEMS;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={mode === 'field' ? field?.name : t('mapMenu.mapActions')}
      onKeyDown={handleKeyDown}
      className="fixed z-[var(--aif-z-contextual)] w-56 bg-[var(--aif-bg-page)] rounded-xl shadow-2xl
                 border border-[var(--aif-border-default)] py-1 overflow-hidden
                 animate-slideUp"
      style={{ left: safeX, top: safeY }}
      data-testid="map-context-menu"
      data-context-menu
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-[var(--aif-border-default)]">
        <div className="text-xs font-semibold text-[var(--aif-text-primary)] truncate">
          {mode === 'field' ? `\uD83C\uDF31 ${field?.name}` : `\uD83D\uDDFA\uFE0F ${t('mapMenu.mapActions')}`}
        </div>
      </div>

      {items.map((item, i) => {
        if (!item.label) {
          return (
            <div
              key={`div-${i}`}
              role="separator"
              className="my-1 border-t border-[var(--aif-border-default)]"
            />
          );
        }
        return (
          <button
            key={item.id}
            role="menuitem"
            tabIndex={-1}
            onClick={() => handleItem(item.id)}
            className={`w-full min-h-[44px] px-3 py-2 text-left flex items-center gap-2 text-sm
              hover:bg-[var(--aif-bg-elevated)] focus:bg-[var(--aif-bg-elevated)] focus:outline-none transition
              ${item.danger ? 'text-red-500' : item.muted ? 'text-[var(--aif-text-muted)]' : 'text-[var(--aif-text-primary)]'}`}
            data-testid={`map-menu-${item.id}`}
            data-context-menu-item={item.id}
          >
            <span className="w-5 flex-shrink-0">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
